<template>
    <div class="loyalty-points-viewer">
        <promo-ag-grid
            :row-data="loyaltyPointsData"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
            :grid-style="gridStyle"
            :default-col-def="defaultColDef"
            grid-class="ag-theme-custom__product-details"
            @grid-ready="onGridReady"
            @cell-value-changed="onCellValueChanged"
        />
        <div class="grid-controls">
            <create-new-button
                :disabled="isAddingLoyaltyPoint || isCreateInProgress"
                :btn-text="buttonText"
                background
                @create-new="addNewLoyaltyPoint"
            />
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapState, mapActions } from 'vuex';
import { isNil } from 'lodash';

import LoyaltyPointsActions from '@/js/components/promo-ag-grid/loyalty-points-actions.vue';
import AgCheckbox from '@/js/components/promo-ag-grid/ag-checkbox.vue';
import agGridUtils from '@/js/utils/ag-grid-utils';

export default {
    /* eslint-disable vue/no-unused-components */
    components: {
        AgCheckbox,
        LoyaltyPointsActions,
    },
    localizationKey: 'admin.loyaltyPoints',
    data() {
        return {
            buttonText: this.toSentenceCase(this.$tkey('table.controls.addLoyaltyPoints')),
            isAddingLoyaltyPoint: false,
            isCreateInProgress: false,
            gridApi: null,
            gridOptions: markRaw({
                rowHeight: 35,
                headerHeight: 50,
                paginationAutoPageSize: true,
                pagination: true,
                stopEditingWhenCellsLoseFocus: true,
                columnTypes: {
                    requiredField: agGridUtils.columnTypes.requiredField,
                },
            }),
            gridStyle: 'width: 100%; height: 100%;',
            defaultColDef: {
                editable: true,
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                sortable: true, // All columns default to being sortable.
                unSortIcon: true, // Ensures the sort icon displays all the time (not just when hovered over).
                flex: 1,
                resizable: true,
                filter: true,
                minWidth: 150,
                menuTabs: ['filterMenuTab'],
            },
        };
    },
    computed: {
        ...mapState('loyaltyPoints', ['loyaltyPoints']),
        loyaltyPointsData() {
            return this.loyaltyPoints;
        },
        columnDefs() {
            return [
                {
                    headerName: `${this.toSentenceCase(this.$tkey('table.headers.identifier'))} *`,
                    field: 'identifier',
                    type: 'requiredField',
                },
                {
                    headerName: `${this.toSentenceCase(this.$tkey('table.headers.description'))} *`,
                    field: 'description',
                    type: 'requiredField',
                },
                {
                    headerName: this.toSentenceCase(this.$tkey('table.headers.isAward')),
                    field: 'isAward',
                    cellRenderer: AgCheckbox,
                    editable: false,
                    cellClass: 'flex-cell-start',
                    maxWidth: 140,
                    cellClassRules: {
                        'invalid-background': this.validateRequiredSelection,
                    },
                },
                {
                    headerName: this.toSentenceCase(this.$tkey('table.headers.isRedeem')),
                    field: 'isRedeem',
                    cellRenderer: AgCheckbox,
                    editable: false,
                    cellClass: 'flex-cell-start',
                    maxWidth: 140,
                    cellClassRules: {
                        'invalid-background': this.validateRequiredSelection,
                    },
                },
                {
                    headerName: this.toSentenceCase(this.$tkey('table.headers.isActive')),
                    field: 'isActive',
                    cellRenderer: AgCheckbox,
                    editable: false,
                    cellClass: 'flex-cell-start',
                    maxWidth: 110,
                },
                {
                    headerName: '',
                    cellRenderer: LoyaltyPointsActions,
                    cellRendererParams: {
                        onCancelCreation: this.enableGridControls,
                    },
                    editable: false,
                    sortable: false,
                    filter: false,
                    resizable: false,
                    minWidth: 30,
                    width: 30,
                    cellClass: 'flex-cell-center',
                },
            ];
        },
    },
    methods: {
        ...mapActions('loyaltyPoints', ['createLoyaltyPoint', 'updateLoyaltyPoint']),
        onGridReady(params) {
            this.gridApi = params.api;
        },
        async onCellValueChanged({ data }) {
            const { identifier, description, isAward, isRedeem, isActive, _id } = data;

            // Check if the row is ready for saving (i.e. all fields have a value).
            const isIdentifierInvalid = isNil(identifier) || identifier.trim() === '';
            const isDescriptionInvalid = isNil(description) || description.trim() === '';
            if (isIdentifierInvalid || isDescriptionInvalid) {
                return;
            }

            // The row is ready to be saved. Determine whether it is a CREATE or UPDATE.
            const isCreate = isNil(_id);
            const loyaltyPointsSaving = {
                identifier,
                description,
                isAward,
                isRedeem,
                isActive,
            };

            if (isCreate) {
                if (!this.isCreateInProgress) {
                    this.isCreateInProgress = true;
                    await this.createLoyaltyPoint({ document: loyaltyPointsSaving });

                    this.isCreateInProgress = false;

                    this.enableGridControls();
                }
            } else {
                await this.updateLoyaltyPoint({
                    id: _id,
                    updates: loyaltyPointsSaving,
                });
            }
        },
        addNewLoyaltyPoint() {
            const newLoyaltyPointRecord = [
                {
                    identifier: null,
                    description: null,
                    isAward: true,
                    isRedeem: true,
                    isActive: true,
                },
            ];
            this.addLoyaltyPointToGrid({
                loyaltyPoint: newLoyaltyPointRecord,
                editColumnKey: 'identifier',
            });
        },
        addLoyaltyPointToGrid({ loyaltyPoint, editColumnKey }) {
            if (!this.gridApi) {
                return;
            }

            const newRow = this.gridApi.applyTransaction({ add: loyaltyPoint });

            // New loyalty points are placed at the end of the grid, so navigate to the last page so the new row is visible.
            this.gridApi.paginationGoToLastPage();
            this.gridApi.ensureIndexVisible(newRow.add[0].rowIndex);

            // Start editing as soon as the row is added.
            this.gridApi.startEditingCell({
                rowIndex: newRow.add[0].rowIndex,
                colKey: editColumnKey,
            });
            this.addedRowIndex = newRow.add[0].rowIndex;

            // Stop users from adding multiple items to the grid at once.
            this.disableGridControls();
        },
        disableGridControls() {
            this.isAddingLoyaltyPoint = true;
        },
        enableGridControls() {
            this.isAddingLoyaltyPoint = false;
        },
        validateRequiredSelection(params) {
            const { isAward, isRedeem } = params.data;
            return !isAward && !isRedeem;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.loyalty-points-viewer {
    @include flex-column;
    height: 100%;

    padding-bottom: 6rem;
    background-color: $promo-white;

    .grid-controls {
        font-size: 1.4rem;
        padding-left: 3rem;
    }
}

.loyalty-points-viewer {
    :deep(.ag-theme-custom) {
        max-width: 50% !important;
    }

    :deep(.flex-cell-start) {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    :deep(.flex-cell-center) {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    :deep(.ag-center-cols-viewport) {
        overflow-x: hidden;
    }

    :deep(.invalid-background) {
        background: $rtls-negative-action-background;
    }
}

:deep(.ag-theme-custom__product-details) {
    .ag-paging-number,
    .ag-paging-row-summary-panel-number {
        font-weight: 700;
    }

    .ag-paging-button.ag-disabled .ag-icon {
        color: var(--ag-disabled-foreground-color, rgba(24, 29, 31, 0.5));
    }
}
</style>
